/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Fragment } from "react";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import { useScrapLoadingLayout } from "components/scrapEyeLabelling/hooks/useScrapLoadingLayout";
import {
  RailCarLayoutArea,
  RailCarLayoutData,
  RailCarLayoutTrack,
} from "src/store/api/generatedApi";
import {
  AREA_ITEM_SIZES,
  AREA_SPACER_LENGTH,
  AreaColorMap,
  AreaMaterialsMap,
  RAILCAR_CONNECTOR_LENGTH,
  RAILCAR_CONNECTOR_THICKNESS,
  TRACK_END_BAR_THICKNESS,
  TRACK_END_CONNECTOR_LENGTH,
  UNMAPPED_AREA_COLOR,
} from "./scrapEyeLayoutCommon";

const BAY_ASPECT_RATIO = 66 / 42;
const RAILCAR_ASPECT_RATIO = 48 / 33;
const ECS_ASPECT_RATIO = 54 / 185;

type ScrapEyeLayoutMapMiniProps = Readonly<{
  highlightedArea?: string | null;
  location?: string;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
}>;

const shapeSize = (area: RailCarLayoutArea) => {
  const shape = area.shape === "landmark" ? "square" : area.shape;
  const base = AREA_ITEM_SIZES[shape].small;
  // SquareItem is the only full-map shape that reads the per-area overrides.
  return shape === "square"
    ? { width: area.width ?? base.width, height: area.height ?? base.height }
    : base;
};

const isVisible = (area: { shape: string }) => area.shape !== "landmark";

const trackWidth = (track: RailCarLayoutTrack) =>
  Math.max(
    ...track.areas.filter(isVisible).map((area) => shapeSize(area).width)
  );

const MiniAreaItem = ({
  label,
  isHighlighted,
  overrideColor,
  material,
  aspectRatio,
  widthPercent = 100,
  isCircle = false,
  grow = true,
}: {
  label: string;
  isHighlighted: boolean;
  overrideColor?: string;
  material?: string;
  aspectRatio: number;
  widthPercent?: number;
  isCircle?: boolean;
  grow?: boolean;
}) => {
  const color = overrideColor ?? UNMAPPED_AREA_COLOR;

  const item = (
    <Box
      sx={{
        flex: grow ? 1 : "0 0 auto",
        width: `${widthPercent.toString()}%`,
        minHeight: 0,
        aspectRatio: aspectRatio.toString(),
        bgcolor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: isCircle ? "50%" : 0.5,
        border: isHighlighted ? 2 : 1,
        borderColor: isHighlighted ? "error.main" : "divider",
        transition: "all 0.2s ease-in-out",
        overflow: "hidden",
        boxShadow: isHighlighted
          ? "0 0 0 2px rgba(211, 47, 47, 0.4), 0 2px 8px rgba(0,0,0,0.3)"
          : undefined,
      }}
    >
      <Typography
        sx={{
          color: "white",
          fontWeight: "bold",
          textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
          fontSize: "0.6rem",
          lineHeight: 1,
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (material) {
    return (
      <Tooltip title={material} arrow placement="top">
        {item}
      </Tooltip>
    );
  }

  return item;
};

const MiniSpacer = () => (
  <Box
    sx={{
      flex: 1,
      width: "100%",
      minHeight: 0,
      aspectRatio: BAY_ASPECT_RATIO.toString(),
    }}
  />
);

const MiniEcsItem = ({ isHighlighted }: { isHighlighted: boolean }) => (
  <Box
    sx={{
      flex: 1,
      width: "100%",
      minHeight: 0,
      aspectRatio: ECS_ASPECT_RATIO.toString(),
      bgcolor: "grey.600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 0.5,
      border: isHighlighted ? 2 : 1,
      borderColor: isHighlighted ? "error.main" : "grey.800",
      transition: "all 0.2s ease-in-out",
      boxShadow: isHighlighted
        ? "0 0 0 2px rgba(211, 47, 47, 0.4), 0 2px 8px rgba(0,0,0,0.3)"
        : undefined,
    }}
  >
    <Typography
      sx={{
        color: "white",
        fontWeight: "bold",
        fontSize: "0.7rem",
      }}
    >
      ECS
    </Typography>
  </Box>
);

const MiniBayLayout = ({
  columns,
  highlightedArea,
  areaColors,
  areaMaterials,
}: {
  columns: readonly (readonly (string | null)[])[];
  highlightedArea?: string | null;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        gap: 0.5,
        p: 1,
        bgcolor: "background.default",
        borderRadius: 1,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {columns.map((column, columnIndex) => {
        const columnKey =
          columnIndex.toString() +
          column
            .filter((n) => n !== null && n !== "ECS" && n !== "Cab")
            .join("-");
        return (
          <Stack
            spacing={0.5}
            alignItems="center"
            key={columnKey}
            sx={{ flex: 1, minWidth: 0, height: "100%" }}
          >
            {column.map((name, index) => {
              if (name === null) {
                return <MiniSpacer key={index.toString() + "spacer"} />;
              }

              const isHighlighted =
                highlightedArea?.toLowerCase() === name.toLowerCase();
              const overrideColor =
                areaColors?.[name.toLowerCase()] ?? areaColors?.[name];
              const material =
                areaMaterials?.[name.toLowerCase()] ?? areaMaterials?.[name];
              if (name === "ECS") {
                return <MiniEcsItem key={name} isHighlighted={isHighlighted} />;
              }
              return (
                <MiniAreaItem
                  key={name}
                  label={formatArea(name)}
                  isHighlighted={isHighlighted}
                  overrideColor={overrideColor}
                  material={material}
                  aspectRatio={BAY_ASPECT_RATIO}
                />
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};

const MiniRailCarLayout = ({
  layout,
  highlightedArea,
  areaColors,
  areaMaterials,
}: {
  layout: {
    tracks: {
      name: string;
      areas: { area_id: string; shape: string; display_name?: string }[];
    }[];
  };
  highlightedArea?: string | null;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      p: 1,
      bgcolor: "background.default",
      borderRadius: 1,
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
    }}
  >
    {layout.tracks
      .filter((track) => track.areas.some((a) => a.shape !== "landmark"))
      .map((track) => (
        <Stack
          key={track.name}
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Typography
            variant="caption"
            sx={{
              width: 48,
              fontSize: "0.7rem",
              fontWeight: "bold",
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            {track.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 0.25,
              flex: 1,
              minWidth: 0,
              height: "100%",
            }}
          >
            {track.areas
              .filter((a) => a.shape !== "landmark")
              .map((area) => {
                const isHighlighted =
                  highlightedArea?.toLowerCase() === area.area_id.toLowerCase();
                const overrideColor =
                  areaColors?.[area.area_id.toLowerCase()] ??
                  areaColors?.[area.area_id];
                const material =
                  areaMaterials?.[area.area_id.toLowerCase()] ??
                  areaMaterials?.[area.area_id];
                return (
                  <Box
                    key={area.area_id}
                    sx={{ flex: 1, minWidth: 0, height: "100%" }}
                  >
                    <MiniAreaItem
                      label={area.display_name ?? formatArea(area.area_id)}
                      isHighlighted={isHighlighted}
                      overrideColor={overrideColor}
                      material={material}
                      aspectRatio={RAILCAR_ASPECT_RATIO}
                    />
                  </Box>
                );
              })}
          </Box>
        </Stack>
      ))}
  </Box>
);

/**
 * Reserves vertical space in proportion to the column, so the whole diagram
 * scales together. Omitting `lineWidth` leaves the space empty.
 */
const MiniVerticalConnector = ({
  columnWidth,
  height,
  lineWidth,
  color,
}: {
  columnWidth: number;
  height: number;
  lineWidth?: string;
  color?: string;
}) => (
  <Box
    sx={{
      width: "100%",
      aspectRatio: `${columnWidth.toString()} / ${height.toString()}`,
      flexShrink: 0,
      display: "flex",
      justifyContent: "center",
    }}
  >
    {lineWidth !== undefined && (
      <Box
        sx={{
          width: lineWidth,
          height: "100%",
          bgcolor: color,
          borderRadius: "2px",
        }}
      />
    )}
  </Box>
);

const MiniRailCarColumnTrack = ({
  track,
  highlightedArea,
  areaColors,
  areaMaterials,
}: {
  track: RailCarLayoutTrack;
  highlightedArea?: string | null;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
}) => {
  // Connector placement is decided after landmarks are dropped, so cars either
  // side of one still read as joined. The full map keeps landmarks and breaks
  // the run.
  const areas = track.areas.filter(isVisible);
  const columnWidth = trackWidth(track);
  const hasRailcar = areas.some((area) => area.shape === "railcar");
  const endsWithRailcar = areas.at(-1)?.shape === "railcar";
  const trackEnd = (
    <MiniVerticalConnector
      columnWidth={columnWidth}
      height={TRACK_END_CONNECTOR_LENGTH}
      lineWidth={`${TRACK_END_BAR_THICKNESS.toString()}px`}
      color="grey.500"
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexGrow: columnWidth,
        flexBasis: 0,
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.6rem",
          fontWeight: "bold",
          color: "text.secondary",
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {track.name}
      </Typography>
      {hasRailcar ? trackEnd : null}
      {areas.map((area, index) => {
        const { width, height } = shapeSize(area);
        const nextArea = areas[index + 1];
        const isHighlighted =
          highlightedArea?.toLowerCase() === area.area_id.toLowerCase();
        const joinsNextRailcar =
          area.shape === "railcar" && nextArea?.shape === "railcar";
        return (
          <Fragment key={area.area_id}>
            <MiniAreaItem
              label={area.display_name ?? formatArea(area.area_id)}
              isHighlighted={isHighlighted}
              overrideColor={
                areaColors?.[area.area_id.toLowerCase()] ??
                areaColors?.[area.area_id]
              }
              material={
                areaMaterials?.[area.area_id.toLowerCase()] ??
                areaMaterials?.[area.area_id]
              }
              aspectRatio={width / height}
              widthPercent={(width / columnWidth) * 100}
              isCircle={area.shape === "circle"}
              grow={false}
            />
            {nextArea === undefined ? null : joinsNextRailcar ? (
              <MiniVerticalConnector
                columnWidth={columnWidth}
                height={RAILCAR_CONNECTOR_LENGTH}
                lineWidth={`${((RAILCAR_CONNECTOR_THICKNESS / columnWidth) * 100).toString()}%`}
                color="grey.600"
              />
            ) : (
              <MiniVerticalConnector
                columnWidth={columnWidth}
                height={AREA_SPACER_LENGTH}
              />
            )}
          </Fragment>
        );
      })}
      {endsWithRailcar ? trackEnd : null}
    </Box>
  );
};

/**
 * Column-orientation railcar layout: tracks run left to right and are aligned
 * on their bottom edge, matching the full-size map in `RailCarLayout.tsx`.
 */
const MiniRailCarColumnLayout = ({
  layout,
  highlightedArea,
  areaColors,
  areaMaterials,
}: {
  layout: RailCarLayoutData;
  highlightedArea?: string | null;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-end",
      gap: 1,
      p: 1,
      bgcolor: "background.default",
      borderRadius: 1,
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    {layout.tracks
      .filter((track) => track.areas.some(isVisible))
      .map((track) => (
        <MiniRailCarColumnTrack
          key={track.name}
          track={track}
          highlightedArea={highlightedArea}
          areaColors={areaColors}
          areaMaterials={areaMaterials}
        />
      ))}
  </Box>
);

export const ScrapEyeLayoutMapMini = ({
  highlightedArea,
  location = "crane",
  areaColors,
  areaMaterials,
}: ScrapEyeLayoutMapMiniProps) => {
  const { layout, layoutType, isLoading } = useScrapLoadingLayout(location);

  if (isLoading || !layout || !layoutType) {
    return null;
  }

  if (layoutType === "railcar") {
    if (layout.orientation === "column") {
      return (
        <MiniRailCarColumnLayout
          layout={layout}
          highlightedArea={highlightedArea}
          areaColors={areaColors}
          areaMaterials={areaMaterials}
        />
      );
    }

    return (
      <MiniRailCarLayout
        layout={layout}
        highlightedArea={highlightedArea}
        areaColors={areaColors}
        areaMaterials={areaMaterials}
      />
    );
  }

  if (layoutType === "grid") {
    return null;
  }

  return (
    <MiniBayLayout
      columns={layout.columns}
      highlightedArea={highlightedArea}
      areaColors={areaColors}
      areaMaterials={areaMaterials}
    />
  );
};
